import keys from '@debbie/common/dist/cache/keys.js';
import { CaseStatus, SystemEndReason, } from '@debbie/common/dist/cases/index.js';
import { useMutation } from '@debbie/common/dist/util/hooks.js';
import * as casesAPI from '@debbie/cortex/dist/api/cases.js';
import { casesEntity, ensureCaseLoaded, } from '@debbie/cortex/dist/cases/index.js';
import { customersEntity, ensureCustomerLoaded, } from '@debbie/cortex/dist/customers/index.js';
import { useEndReasons } from '@debbie/cortex/dist/end-reasons/index.js';
import { h } from '@debbie/cortex/dist/index.js';
import q from '@debbie/cortex/dist/query.js';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import THEME from '../theme.js';
import { errorNotification } from '../utils/errors.js';
import { FeatherIconsUnqiueMatch } from './DesignSystem/elements/FeatherIcons.js';
import { CATEGORY_COLOR, CATEGORY_ORDER } from './end-reasons/categories.js';
import { Col, Row } from './Grid/index.js';
import { Button, ButtonGroup, Input, Messages, Modal } from './index.js';
const CaseStatusSelector = (props) => {
    const singleCase = props.case;
    const lang = useSelector((s) => s.lang);
    const endReasonsSource = useEndReasons();
    const [endModalOpen, setEndModalOpen] = useState(false);
    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errors, setErrors] = useState([]);
    const [query, setQuery] = useState('');
    const [activeIndex, setActiveIndex] = useState(0);
    const toggleEndModal = () => {
        setEndModalOpen(!endModalOpen);
    };
    const toggleErrorModal = () => {
        setErrorModalOpen(!errorModalOpen);
    };
    const changeCaseStatusMutation = useMutation(async ({ status, endReasonId, }) => {
        await casesAPI.patch(singleCase.caseId, {
            status,
            endReasonId,
        });
        // Reload case
        q.invalidate(keys.write.modify(casesEntity, singleCase.caseId));
        q.invalidate(keys.write.modify(customersEntity, singleCase.customerId));
        await ensureCaseLoaded(singleCase.caseId);
        await ensureCustomerLoaded(singleCase.customerId);
    }, {
        onSuccess: () => {
            setErrors([]);
            setEndModalOpen(false);
            setErrorModalOpen(false);
        },
        onError: (err) => {
            errorNotification(lang, err, 'update', lang.errorEntities.case);
        },
    });
    const changeCaseStatus = (status, endReasonId) => {
        changeCaseStatusMutation.mutate({ status, endReasonId });
    };
    const onEnd = (endReasonId) => {
        changeCaseStatus(CaseStatus.ENDED, endReasonId);
    };
    const endReasons = (h.hasData(endReasonsSource) ? endReasonsSource.data : []).filter((reason) => {
        if ([SystemEndReason.FireAndForget, SystemEndReason.Deleted].includes(reason.handle)) {
            return false;
        }
        return (!query ||
            `${lang.endReason[reason.handle] ?? reason.label}`
                .toLowerCase()
                .indexOf(query.toLowerCase()) !== -1);
    });
    const groupedEndReasons = useMemo(() => {
        const byCategory = new Map();
        const uncategorized = [];
        endReasons.forEach((reason) => {
            if (reason.category) {
                const existing = byCategory.get(reason.category) ?? [];
                existing.push(reason);
                byCategory.set(reason.category, existing);
            }
            else {
                uncategorized.push(reason);
            }
        });
        return { byCategory, uncategorized };
    }, [endReasons]);
    // Groups in render order. Each group renders as its own 6-column row, so
    // arrow navigation has to treat them as separate sub-grids — a flat ±6 jump
    // would drift sideways every time it crosses a category boundary.
    const renderGroups = useMemo(() => {
        const groups = [];
        for (const category of CATEGORY_ORDER) {
            const group = groupedEndReasons.byCategory.get(category);
            if (group && group.length > 0) {
                groups.push(group);
            }
        }
        if (groupedEndReasons.uncategorized.length > 0) {
            groups.push(groupedEndReasons.uncategorized);
        }
        return groups;
    }, [groupedEndReasons]);
    const orderedEndReasons = useMemo(() => renderGroups.flat(), [renderGroups]);
    const renderGroup = (group) => (React.createElement(Row, null, group.map((reason) => {
        const flatIndex = orderedEndReasons.indexOf(reason);
        return (React.createElement(EndReasonButton, { key: reason.id, endReason: reason, onClick: () => onEnd(reason.id), active: flatIndex === activeIndex }));
    })));
    return (React.createElement("div", null,
        [CaseStatus.PENDING, CaseStatus.VERIFIED].includes(singleCase.status) && (React.createElement(ButtonGroup, null,
            CaseStatus.PENDING === singleCase.status && (React.createElement(Button, { theme: "secondary", loading: changeCaseStatusMutation.isLoading, size: "small", className: "ui-test-48a86ed2", onClick: () => {
                    changeCaseStatus(CaseStatus.VERIFIED);
                } }, lang.cases.verify)),
            React.createElement(Button, { theme: "secondary", loading: changeCaseStatusMutation.isLoading, size: "small", onClick: toggleEndModal }, lang.cases.end))),
        [CaseStatus.ENDED].includes(singleCase.status) && (React.createElement(ButtonGroup, null,
            React.createElement(Button, { theme: "secondary", loading: changeCaseStatusMutation.isLoading, size: "small", onClick: () => {
                    changeCaseStatus(CaseStatus.VERIFIED);
                } }, lang.cases.reVerify))),
        endModalOpen && (React.createElement(Modal, { maxWidth: "1300px", header: lang.cases.endCase, onToggle: toggleEndModal },
            React.createElement(Messages, { messages: errors, type: 'error' }),
            React.createElement("h3", null, lang.cases.choosEndReason),
            React.createElement(Input, { placeholder: "S\u00F8g", autofocus: true, value: query, onChange: (e) => {
                    setQuery(e.target.value);
                    setActiveIndex(0);
                }, onKeyDown: (e) => {
                    const k = e.key;
                    if (k == 'Enter') {
                        const endReason = orderedEndReasons[activeIndex];
                        if (endReason) {
                            onEnd(endReason.id);
                        }
                        return;
                    }
                    if (k != 'ArrowRight' &&
                        k != 'ArrowLeft' &&
                        k != 'ArrowUp' &&
                        k != 'ArrowDown') {
                        return;
                    }
                    const cols = 6;
                    // Resolve (group, position-within-group) from flat activeIndex.
                    let groupIdx = 0;
                    let posInGroup = activeIndex;
                    while (groupIdx < renderGroups.length &&
                        posInGroup >= renderGroups[groupIdx].length) {
                        posInGroup -= renderGroups[groupIdx].length;
                        groupIdx++;
                    }
                    if (groupIdx >= renderGroups.length) {
                        return;
                    }
                    const flatFromPosition = (g, p) => {
                        let offset = 0;
                        for (let i = 0; i < g; i++) {
                            offset += renderGroups[i].length;
                        }
                        return offset + p;
                    };
                    const col = posInGroup % cols;
                    const row = Math.floor(posInGroup / cols);
                    let newGroup = groupIdx;
                    let newPos = posInGroup;
                    if (k == 'ArrowRight') {
                        if (activeIndex + 1 < orderedEndReasons.length) {
                            e.preventDefault();
                            setActiveIndex(activeIndex + 1);
                        }
                        return;
                    }
                    if (k == 'ArrowLeft') {
                        if (activeIndex > 0) {
                            e.preventDefault();
                            setActiveIndex(activeIndex - 1);
                        }
                        return;
                    }
                    if (k == 'ArrowDown') {
                        const group = renderGroups[groupIdx];
                        const lastRow = Math.floor((group.length - 1) / cols);
                        if (row < lastRow) {
                            newPos = Math.min((row + 1) * cols + col, group.length - 1);
                        }
                        else if (groupIdx + 1 < renderGroups.length) {
                            newGroup = groupIdx + 1;
                            newPos = Math.min(col, renderGroups[newGroup].length - 1);
                        }
                        else {
                            return;
                        }
                    }
                    if (k == 'ArrowUp') {
                        if (row > 0) {
                            newPos = (row - 1) * cols + col;
                        }
                        else if (groupIdx > 0) {
                            newGroup = groupIdx - 1;
                            const prev = renderGroups[newGroup];
                            const prevLastRow = Math.floor((prev.length - 1) / cols);
                            newPos = Math.min(prevLastRow * cols + col, prev.length - 1);
                        }
                        else {
                            return;
                        }
                    }
                    e.preventDefault();
                    setActiveIndex(flatFromPosition(newGroup, newPos));
                } }),
            CATEGORY_ORDER.map((category) => {
                const group = groupedEndReasons.byCategory.get(category);
                if (!group || group.length === 0) {
                    return null;
                }
                return (React.createElement(CategorySection, { key: category },
                    React.createElement(CategoryHeader, { $color: CATEGORY_COLOR[category] },
                        React.createElement("span", { className: "dot" }),
                        React.createElement("span", null, lang.endReasonCategory[category])),
                    renderGroup(group)));
            }),
            groupedEndReasons.uncategorized.length > 0 && (React.createElement(CategorySection, null,
                React.createElement(CategoryHeader, { $color: THEME.COLOR_GREY },
                    React.createElement("span", { className: "dot" }),
                    React.createElement("span", null, lang.endReasonsSettings.uncategorized)),
                renderGroup(groupedEndReasons.uncategorized))))),
        errorModalOpen && (React.createElement(Modal, { maxWidth: "400px", header: "Errors", onToggle: toggleErrorModal },
            React.createElement(Messages, { messages: errors, type: "error" })))));
};
const iconEndReasonMatch = new Map([
    [SystemEndReason.AddressUnknown, 'Home'],
    [SystemEndReason.Bankruptcy, 'TrendingDown'],
    [SystemEndReason.ConflictOfInterest, 'Maximize2'],
    [SystemEndReason.CreditorUnresponsive, 'PhoneOff'],
    [SystemEndReason.Dead, 'Twitter'],
    [SystemEndReason.DebtRelief, 'Archive'],
    [SystemEndReason.Deleted, 'Trash2'],
    [SystemEndReason.NoDebt, 'AlertOctagon'],
    [SystemEndReason.Departed, 'Briefcase'],
    [SystemEndReason.ErrorCreditor, 'RotateCcw'],
    [SystemEndReason.ErrorUs, 'AlertCircle'],
    [SystemEndReason.Expiration, 'Watch'],
    [SystemEndReason.FireAndForget, 'EyeOff'],
    [SystemEndReason.ForcedDissolved, 'Meh'],
    [SystemEndReason.Irrecoverable, 'Frown'],
    [SystemEndReason.Merged, 'Minimize2'],
    [SystemEndReason.Minor, 'GitMerge'],
    [SystemEndReason.NegativePayoff, 'Percent'],
    [SystemEndReason.Objection, 'PhoneIncoming'],
    [SystemEndReason.OfferFromDebtor, 'Mail'],
    [SystemEndReason.OfferFromUs, 'FileText'],
    [SystemEndReason.Overpayment, 'ShoppingCart'],
    [SystemEndReason.Paid, 'CheckCircle'],
    [SystemEndReason.PartiallyPaid, 'Check'],
    [SystemEndReason.SmallCaseNotWanted, 'XCircle'],
    [SystemEndReason.Transferred, 'Share'],
    [SystemEndReason.Unidentified, 'UserX'],
    [SystemEndReason.Withdrawn, 'FileMinus'],
    [SystemEndReason.NoCourt, 'List'],
]);
const EndReasonButton = (props) => {
    const lang = useSelector((s) => s.lang);
    return (React.createElement(React.Fragment, null,
        React.createElement(Col, { width: "2" },
            React.createElement(EndReasonContainer, { active: props.active, role: 'button', onClick: props.onClick },
                React.createElement(FeatherIconsUnqiueMatch, { query: iconEndReasonMatch.get(props.endReason.handle) ?? 'MoreHorizontal' }),
                React.createElement("div", null, lang.endReason[props.endReason.handle] ??
                    props.endReason.label)))));
};
const CategorySection = styled.div `
  margin-bottom: 20px;
`;
const CategoryHeader = styled.div `
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0 0 12px;
  font-size: 0.85em;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => p.$color};

  .dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(p) => p.$color};
  }
`;
const EndReasonContainer = styled.div `
  display: flex;
  gap: 30px;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  height: 150px;
  padding: 10px;
  margin-bottom: 10px;
  box-shadow: 0 20px 70px -30px rgba(0, 0, 0, 0.15);
  border-radius: 3px;
  background-color: ${(props) => (props.active ? THEME.FILL : '#ffffff')};
  border: 1px solid ${THEME.BORDER_STRONG};
  transform: translateY(0px);
  transition: transform 0.2s;
  cursor: pointer;
  overflow: hidden;

  & > svg {
    height: 2em;
    width: 2em;
  }

  &:hover {
    transform: translateY(-3px);
  }
`;
export default CaseStatusSelector;
//# sourceMappingURL=CaseStatusSelector.js.map